import type { RawVisualGrouping } from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";

export type RatingRaw = RawVisualGrouping & {
  type: "rating";
  content: string;
  style: "button" | "slider" | "star";
  options: { value: number; text: string; display_text: boolean }[];
  dont_know_option: { value: string; text: string } | null;
  previous_response?: string | number;
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
};

export type RatingDefinition = {
  prompt: string;
  options: readonly { value: number; label: string; displayLabel: boolean }[];
  dontKnow?: { value: string; label: string };
  previousResponse?: string | number;
  image?: string;
  altText?: string;
  error?: string;
};

export type RatingDraft = { selectedValue: number | string | null };
export type RatingEvent =
  | { type: "select"; value: number }
  | { type: "dont-know" }
  | { type: "clear" };
export type RatingResponse = { type: "rating"; value: string };

const responseValue = (definition: RatingDefinition, draft: RatingDraft) => {
  if (draft.selectedValue === definition.dontKnow?.value)
    return definition.dontKnow.value;
  const option = definition.options.find(
    (item) => item.value === draft.selectedValue,
  );
  return option === undefined ? undefined : String(option.value);
};

export const ratingCore: QuestionCore<
  RatingRaw,
  RatingDefinition,
  RatingDraft,
  RatingEvent,
  RatingResponse
> = {
  decode: (raw) => {
    if (!Array.isArray(raw.options) || raw.options.length === 0) {
      throw new Error("Rating requires at least one point.");
    }
    const values = raw.options.map((option) => option.value);
    if (
      values.some((value) => !Number.isInteger(value)) ||
      new Set(values).size !== values.length
    ) {
      throw new Error("Rating points must have unique integer values.");
    }
    if (
      raw.previous_response !== undefined &&
      typeof raw.previous_response !== "string" &&
      typeof raw.previous_response !== "number"
    ) {
      throw new Error("Rating previous response must be a string or number.");
    }
    return {
      prompt: raw.content,
      options: raw.options.map((option) => ({
        value: option.value,
        label: option.text,
        displayLabel: option.display_text,
      })),
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.previous_response !== undefined
        ? { previousResponse: raw.previous_response }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => {
    const previous = definition.previousResponse;
    if (definition.dontKnow && previous === definition.dontKnow.value) {
      return { selectedValue: definition.dontKnow.value };
    }
    const point = definition.options.find(
      (option) => String(option.value) === String(previous),
    );
    return { selectedValue: point?.value ?? null };
  },
  update: (definition, draft, event) => {
    if (event.type === "clear") return { selectedValue: null };
    if (event.type === "dont-know") {
      return definition.dontKnow
        ? { selectedValue: definition.dontKnow.value }
        : draft;
    }
    return definition.options.some((option) => option.value === event.value)
      ? { selectedValue: event.value }
      : draft;
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) =>
    responseValue(definition, draft) === undefined
      ? { valid: false, error: copy.validation.rating }
      : { valid: true },
  encode: (definition, draft) => {
    const value = responseValue(definition, draft);
    if (value === undefined) throw new Error("Select a rating.");
    return { type: "rating", value };
  },
  projectTranscript: (definition, draft) => {
    const value = responseValue(definition, draft);
    if (value === undefined)
      throw new Error("Cannot project an unanswered rating.");
    const option = definition.options.find(
      (item) => String(item.value) === value,
    );
    return {
      kind: "rating",
      question: definition.prompt,
      answer: option
        ? option.displayLabel && option.label
          ? `${value} (${option.label})`
          : value
        : definition.dontKnow!.label,
    };
  },
};
